import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, ExternalLink, Sun, Moon, ChevronDown, Bell, TrendingUp, Users, Flag, Info, ArrowLeft } from 'lucide-react';
import { useDarkMode } from './DarkModeContext';
import { usePoliticianData } from './PoliticianDataContext';
import UpdateDetailModal from './UpdateDetailModal';

const fontStyle = document.createElement('style');
fontStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
`;
document.head.appendChild(fontStyle);

const formatDate = (dateString) => {
  if (!dateString) return '';
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return 'Unknown';
  return `${months[month - 1]} ${day}, ${year}`;
};

const ProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode, setIsDarkMode } = useDarkMode();
  const { politicians, politicianUpdates, availableTags, loading, error } = usePoliticianData();
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [selectedUpdate, setSelectedUpdate] = useState(null);

  const politician = politicians.find(p => String(p.id) === String(id));
  const updates = politician ? (politicianUpdates[politician.id] || []) : [];

  // Tag count and sorting logic from old App.jsx
  const getTagCounts = (politicianId) => {
    const updates = politicianUpdates[politicianId] || [];
    const counts = {};
    availableTags.forEach(tag => { counts[tag.name] = 0; });
    updates.forEach(update => {
      if (update.tags && Array.isArray(update.tags)) {
        update.tags.forEach(tag => { if (counts[tag] !== undefined) { counts[tag]++; } });
      }
    });
    return counts;
  };

  // Tag bar sorting: by frequency, "Other" always last
  const getSortedTags = () => {
    const tagCounts = getTagCounts(politician.id);
    let sortedTags = [...availableTags]
      .map(tag => ({ ...tag, count: tagCounts[tag.name] || 0 }))
      .sort((a, b) => {
        if (a.name === 'Other') return 1;
        if (b.name === 'Other') return -1;
        return b.count - a.count || a.name.localeCompare(b.name);
      });
    return sortedTags;
  };

  const handleTagToggle = (tagName) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) return prev.filter(t => t !== tagName);
      return [...prev, tagName];
    });
  };

  const currentUpdates = updates.filter(update => {
    if (selectedTags.length === 0) return true;
    return update.tags && selectedTags.some(tag => update.tags.includes(tag));
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !politician) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Profile</h2>
          <p className="text-gray-600 mb-4">{error || 'Profile not found.'}</p>
          <button 
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Layout: true two-column, flat, no card wrappers, as in old App.jsx
  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}> 
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}> 
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4"> 
          <div className="flex items-center justify-between"> 
            <div className="flex items-center space-x-4"> 
              <button 
                onClick={() => navigate('/')} 
                className={`flex items-center px-4 py-2 ${
                  politician.party === 'Republican'
                    ? 'bg-gradient-to-r from-red-500 to-red-700 text-white'
                    : politician.party === 'Democrat'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-200 text-gray-700'
                } shadow-md rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-x-1`}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                <span>Back to Home</span>
              </button>
              <div className="flex items-center space-x-3">
                <img
                  src="/title.png"
                  alt="CivicDuty Logo"
                  className="h-10 w-auto cursor-pointer"
                  style={{ maxHeight: '2.5rem' }}
                  onClick={() => navigate('/')}
                />
              </div>
            </div> 
            <button 
              onClick={() => setIsDarkMode(!isDarkMode)} 
              className={`p-2 rounded-lg transition-all duration-200 ${ 
                isDarkMode 
                  ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
              }`} 
              aria-label="Toggle dark mode"> 
              {isDarkMode ? ( 
                <Sun className="w-5 h-5" /> 
              ) : ( 
                <Moon className="w-5 h-5" /> 
              )} 
            </button> 
          </div> 
        </div> 
      </header>
      <main className="flex h-[calc(100vh-80px)]">
        {/* Left column */}
        <div className={`w-2/5 ${isDarkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-white border-r border-gray-100'} flex flex-col`}>
          <div className={`flex-shrink-0 ${
            politician.party === 'Republican'
              ? isDarkMode
                ? 'bg-gray-900'
                : 'bg-gradient-to-br from-red-50 via-white to-red-50'
              : politician.party === 'Democrat'
                ? isDarkMode
                  ? 'bg-gray-900'
                  : 'bg-gradient-to-br from-blue-50 via-white to-blue-50'
                : isDarkMode
                  ? 'bg-gray-900'
                  : 'bg-white'
          } px-8 py-10 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-50'}`}> 
            <div className="text-center">
              <div className="relative inline-block mb-6">
                <img src={politician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(politician.name)}&background=random&color=fff`} alt={politician.name} className={`w-24 h-24 rounded-full object-cover border-4 mb-3 ${
                  politician.party === 'Republican'
                    ? 'border-red-500'
                    : politician.party === 'Democrat'
                      ? 'border-blue-500'
                      : 'border-gray-500'
                }`} />
              </div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2 leading-tight`}>{politician.name}</h1>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} font-medium mb-3`}>{politician.office}</p>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                politician.party === 'Republican'
                  ? 'bg-gradient-to-r from-red-400 to-red-600 text-white'
                  : politician.party === 'Democrat'
                    ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
                    : isDarkMode
                      ? 'bg-gray-700 text-gray-300'
                      : 'bg-gray-200 text-gray-700'
              } inline-block`}>{politician.party}</span>
            </div>
          </div>
          <div className={`flex-1 px-8 py-6 space-y-6 overflow-y-auto ${politician.party === 'Republican' ? 'republican-scrollbar' : 'democrat-scrollbar'}`}>
            <div className="grid grid-cols-1 gap-4">
              <div className={`flex items-center space-x-3 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                <div className={`w-10 h-10 ${
                  politician.party === 'Republican'
                    ? (isDarkMode ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-red-400 to-red-600')
                    : politician.party === 'Democrat'
                      ? (isDarkMode ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-blue-400 to-blue-600')
                      : 'bg-gray-700'
                } rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Represents</p>
                  <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} truncate`}>{politician.location}</p>
                </div>
              </div>
              <div className={`flex items-center space-x-3 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                <div className={`w-10 h-10 ${
                  politician.party === 'Republican'
                    ? (isDarkMode ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-red-400 to-red-600')
                    : politician.party === 'Democrat'
                      ? (isDarkMode ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-blue-400 to-blue-600')
                      : 'bg-gray-700'
                } rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current Term</p>
                  <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{politician.termStart && politician.termEnd ? `${formatDate(politician.termStart)} - ${formatDate(politician.termEnd)}` : politician.termStart ? `${formatDate(politician.termStart)} - Present` : 'Unknown - Present'}</p>
                </div>
              </div>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100'} rounded-xl p-6 border`}>
              <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 uppercase tracking-wide flex items-center`}>
                <Users className="w-4 h-4 mr-2" />About the Person
              </h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>{politician.description}</p>
            </div>
            {politician.positionDescription && (
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-r from-green-50 to-teal-50 border-green-100'} rounded-xl p-6 border`}>
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 uppercase tracking-wide flex items-center`}>
                  <Flag className="w-4 h-4 mr-2" />About the Position
                </h3>
                <div className="flex items-center mb-4">
                  <span className="inline-flex items-center px-5 py-2 rounded-full text-base font-bold shadow-lg bg-blue-100 text-blue-800" style={{ fontSize: '1.15rem', letterSpacing: '0.04em' }}>
                    <Info className="w-4 h-4 mr-1" />Executive
                  </span>
                </div>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>{politician.positionDescription}</p>
              </div>
            )}
            {politician.website && (
              <div className="px-8 pb-6">
                <a href={politician.website} target="_blank" rel="noopener noreferrer" className={`w-full inline-flex items-center justify-center px-6 py-3 font-medium rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
                  politician.party === 'Republican'
                    ? 'bg-gradient-to-r from-red-500 to-red-700 text-white hover:from-red-600 hover:to-red-800'
                    : politician.party === 'Democrat'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}>
                  <ExternalLink className="w-5 h-5 mr-2" />Visit Official Website
                </a>
              </div>
            )}
          </div>
        </div>
        {/* Right column */}
        <div className={`w-3/5 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex flex-col`}>
          <div className={`flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-b border-gray-700' : 'bg-white border-b border-gray-100'} px-8 py-6`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Updates</h2>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Latest activity and policy positions</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="relative flex items-center">
                    <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg ring-2 ring-green-400"></span>
                  </div>
                  <span className={`text-sm font-bold tracking-wide uppercase drop-shadow-sm animate-pulse ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Live</span>
                </div>
              </div>
            </div>
            {/* Tag bar */}
            <div className={`flex flex-wrap gap-2 overflow-y-auto ${showAllTags ? '' : 'max-h-32'} ${
              politician.party === 'Republican'
                ? 'republican-scrollbar'
                : politician.party === 'Democrat'
                  ? 'democrat-scrollbar'
                  : 'custom-scrollbar'
            }`}> 
              <button className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                selectedTags.length === 0
                  ? (politician.party === 'Republican'
                      ? 'bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md'
                      : politician.party === 'Democrat'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-md'
                        : isDarkMode
                          ? 'bg-gray-700 text-gray-300 shadow-md'
                          : 'bg-white text-gray-700 border border-gray-200 shadow-md')
                  : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'} flex items-center gap-2`}>All<span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-800'}`}>{updates.length}</span></button>
              {(() => {
                const sortedTags = getSortedTags();
                const topTags = sortedTags.slice(0, 5);
                const restTags = sortedTags.slice(5);
                const tagsToShow = showAllTags ? sortedTags : topTags;
                return (
                  <>
                    {tagsToShow.map(tag => (
                      <button
                        key={tag.id || tag._id}
                        onClick={() => handleTagToggle(tag.name)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedTags.includes(tag.name) ? (
                          politician.party === 'Republican'
                            ? 'bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md'
                            : politician.party === 'Democrat'
                              ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-md'
                              : isDarkMode
                                ? 'bg-gray-700 text-gray-300 shadow-md'
                                : 'bg-white text-gray-700 border border-gray-200 shadow-md'
                        ) : isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'} flex items-center gap-2`}
                      >
                        {tag.name}
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{tag.count}</span>
                      </button>
                    ))}
                    {restTags.length > 0 && (
                      <button
                        onClick={() => setShowAllTags(v => !v)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border border-gray-300 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50'} flex items-center gap-2`}
                        aria-label={showAllTags ? 'Show fewer tags' : 'Show all tags'}
                        style={{ minWidth: '2.5rem' }}
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showAllTags ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className={`flex-1 overflow-y-auto px-8 py-6 ${politician.party === 'Republican' ? 'republican-scrollbar' : 'democrat-scrollbar'}`}> 
            <div className="space-y-4">
              {currentUpdates.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <Bell className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
                    <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} mb-2`}>No updates found</h3>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>No approved updates are available yet.</p>
                  </div>
                </div>
              ) : (
                currentUpdates.map((update) => (
                  <div key={update.id} className={`${isDarkMode ? 'bg-gray-800 border hover:border-red-800/50' : 'bg-white border hover:border-red-200'} rounded-xl p-6 transition-all duration-200 cursor-pointer group`} onClick={() => setSelectedUpdate(update)}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}> 
                        <span className="flex items-center text-xs">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-calendar w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} mr-1`} aria-hidden="true"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                          <span>{update.formattedDate || update.date}</span>
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        {update.tags && update.tags.map((tag, index) => (
                          <span key={index} className={`${isDarkMode ? 'bg-fuchsia-900/50 text-fuchsia-300' : 'bg-fuchsia-100 text-fuchsia-800'} px-3 py-1 rounded-full text-xs font-medium`}>{tag}</span>
                        ))}
                      </div>
                    </div>
                    <p className={`leading-relaxed font-medium group-hover:text-red-900 transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{update.summary}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
      <UpdateDetailModal open={!!selectedUpdate} update={selectedUpdate && { ...selectedUpdate, link: selectedUpdate.pressReleaseUrl }} onClose={() => setSelectedUpdate(null)} />
    </div>
  );
};

export default ProfilePage; 